import dayjs from 'dayjs';
import appConfig from 'src/config.json';
import { CompanyDevDashboardDevice, DataTypeEnum, Device, DeviceStatus, MeasurementPoint, MpParam, SensorType } from 'statotest-api-requests';

import featuresConfig from "../../features-config.json";
import type { FeaturesConfig } from '../../types/custom';

const typedFeaturesConfig = featuresConfig as FeaturesConfig;
const featuresFlags = typedFeaturesConfig[__FEATURE_KEY__];

if (!featuresFlags) {
  throw new Error(`Feature key "${__FEATURE_KEY__}" not found in features config`);
}

console.info("Current Application Feature Flag State: ", featuresFlags);
export { featuresFlags };
// console.log("featuresFlags.params['statotest-charts'].appType: ", featuresFlags.params['statotest-charts'].appType);
export const isExtCharts = featuresFlags.params['statotest-charts'].appType === "r-ext-charts";

const notificationApiURL = appConfig.NOTIFICATION_API_URL;

export const colorModes = { theme: "coreui-statotest-theme" }

export function getNotificationApiURL(): string {
  return notificationApiURL;
}

export const parseMpParam = (mpParam: string): MpParam[] => {
  try {
    const trimmedMpParam = mpParam.trim();
    const parsedMpParam = JSON.parse(trimmedMpParam) as unknown as MpParam[];
    if (!Array.isArray(parsedMpParam)) { throw new Error("Parsed mpParam is not an array"); }
    return parsedMpParam;
  } catch (err) {
    console.error("parseMpParam - failed to parse param error: ", err, "tried parsing these params:", mpParam);
    return [];
  }
}

export const handleDownload = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export type MpWithDeviceType = MeasurementPoint & { device?: Device };

export function compareObjs(obj1: object, obj2: object): boolean {
  return JSON.stringify(obj1) === JSON.stringify(obj2)
}

export function degToRad(degrees: number): number {
  const pi = Math.PI
  return degrees * (pi / 180)
}

export const dataTypesNames: Record<DataTypeEnum, string> = {
  [DataTypeEnum.Device]: "Device",
  [DataTypeEnum.Acceleration]: "Accelero",
  [DataTypeEnum.Inclination]: "Inclino",
  [DataTypeEnum.Weather]: "Meteo",
  [DataTypeEnum.Environment]: "Environment",
  [DataTypeEnum.Voltage]: "Voltage",
  [DataTypeEnum.Raw]: "Raw",
  [DataTypeEnum.State]: "State",
  [DataTypeEnum.Strain]: "Strain",
  [DataTypeEnum.CoordinateLocal]: "Coordinate Local",
  [DataTypeEnum.CoordinateJTSK]: "Coordinate JTSK",
  [DataTypeEnum.Unit]: "Unit",

  // 100: "LastComm",
  999: "All",
  // artifical
  [DataTypeEnum.Dilato]: 'Dilato',
  [DataTypeEnum.OnlyTemps]: 'Dew Point',
};


export const isValidEmailAdress = (email: string): boolean => {
  if (!email) { return false; }
  // Regular expression pattern for a valid email address
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  // Test if the provided email matches the pattern
  return emailPattern.test(email);
}


export interface DashboardDeviceWithStatus extends CompanyDevDashboardDevice {
  status: DeviceStatus[];
}

interface ProcessedDevicesStates {
  devBatteryErrorIds: string[];
  devNotComunicatingIds: string[];
  devActiveIds: string[];
  mpNotAssignedDevIds: string[];
  devicesWithStatus: DashboardDeviceWithStatus[];
}
// needs to be data from mp/Dev/get - testing if the mp has device or not
export const processDevicesStates = (devArray: CompanyDevDashboardDevice[]): ProcessedDevicesStates => {
  const batteryError = new Set<string>();
  const notCommunicating = new Set<string>();
  const active = new Set<string>();
  const notAssignedMps = new Set<string>();

  const now = dayjs().unix();
  const devicesWithStatus: DashboardDeviceWithStatus[] = devArray.map((mpDev) => {
    const companyDevMp = mpDev;
    const mpDevStatus: DeviceStatus[] = [];

    if (!companyDevMp.devId || companyDevMp.devStatus === "UnAssigned") {
      notAssignedMps.add(companyDevMp.refMpId ?? "---");
      return { ...companyDevMp, status: ["notAssigned"] }
    }

    if (typeof companyDevMp.devBattery === "number" && companyDevMp.devBattery !== 0 && companyDevMp.devBattery <= 30) {
      // console.log("adding battery error: ", companyDevMp.devBattery);
      batteryError.add(companyDevMp.devId);
      mpDevStatus.push("batteryWarning");
    }

    if ("dateTimeLastComm" in companyDevMp && now > dayjs(companyDevMp.dateTimeLastComm).add(12, "hours").unix()) {
      //console.log("deviceAndMP.dateTimeLastComm: ", companyDevMp.dateTimeLastComm);
      notCommunicating.add(companyDevMp.devId);
      mpDevStatus.push("communication");
    }
    else if (mpDevStatus.length < 1) {
      active.add(companyDevMp.devId);
      mpDevStatus.push("active");
    } else { active.add(companyDevMp.devId); }

    return { ...companyDevMp, status: mpDevStatus };
  });
  // console.log("processDevicesStates - devicesWithStatus: ", devicesWithStatus);

  return {
    devBatteryErrorIds: Array.from(batteryError),
    devNotComunicatingIds: Array.from(notCommunicating),
    devActiveIds: Array.from(active),
    mpNotAssignedDevIds: Array.from(notAssignedMps),

    devicesWithStatus: devicesWithStatus,
  }
}

export interface Option { label: string, value: string, disabled?: boolean }

export interface ParamValidationResult {
  name: boolean;
  value: boolean;
  channel: boolean;
  validFromEpoch: boolean;
  chartType: boolean | string | undefined;
  unitId: boolean;
  sensorTypeId: boolean;
  eventTypeId: boolean;
  measId: boolean | string | undefined;
  application: boolean | undefined;
}

export const validateParam = (parameter: MpParam): ParamValidationResult => ({
  name: !!parameter.name,
  value: !!parameter.value,
  channel: !parameter.channel || (!isNaN(parameter.channel) && parameter.channel > 0 && parameter.channel < 10000),
  validFromEpoch: !parameter.validFromEpoch || (!isNaN(parameter.validFromEpoch) && parameter.validFromEpoch > 0),
  chartType: !("chartType" in parameter) || parameter.chartType,
  unitId: !parameter.unitId || (typeof parameter.unitId === "number" && !isNaN(parameter.unitId) && Number(parameter.unitId) >= 0 && Number(parameter.unitId) < 10000),
  sensorTypeId: !parameter.sensorTypeId || (typeof parameter.sensorTypeId === "number" && !isNaN(parameter.sensorTypeId) && parameter.sensorTypeId >= 0 && parameter.sensorTypeId < 10000),
  eventTypeId: !parameter.eventTypeId || (typeof parameter.eventTypeId === "number" && !isNaN(parameter.eventTypeId) && parameter.eventTypeId >= 0 && parameter.eventTypeId < 10000),
  measId: !("measId" in parameter) || parameter.measId,
  application: !("application" in parameter) || (parameter.application === undefined || !!parameter.application),
});

/**
 * Mapování typů senzorů na překladové klíče (i18n).
 * Tento objekt slouží k převodu číselných nebo enum hodnot na čitelné řetězce,
 * které se následně v UI překládají podle aktuálního jazyka.
 */
export const sensorTypeNames: Record<SensorType, string> = {
  [SensorType.Device]: "sensorTypes.0",
  [SensorType.ChesterBattery]: "sensorTypes.1",
  [SensorType.TMP112]: "sensorTypes.2",
  [SensorType.LIS2DH12]: "sensorTypes.3",
  [SensorType.ADXL355]: "sensorTypes.4",
  [SensorType.SHT30]: "sensorTypes.5",
  [SensorType.ADC12B]: "sensorTypes.6",
  [SensorType.ADC24B]: "sensorTypes.7",
  [SensorType.BME_280]: "sensorTypes.8",
  [SensorType.DS18B20]: "sensorTypes.9",
  [SensorType.CTR_SOIL]: "sensorTypes.10",
  [SensorType.ChesterInterupt]: "sensorTypes.11",
  [SensorType.StringStrain]: "sensorTypes.12",
  [SensorType.HDL300]: "sensorTypes.13",
  [SensorType.DYP_A16]: "sensorTypes.14",
  [SensorType.DYP_A12]: "sensorTypes.15",
  [SensorType.WH1080_SHT30]: "sensorTypes.16",
  [SensorType.WH1080]: "sensorTypes.17",
  [SensorType.HPMA115C0_004]: "sensorTypes.18",
  [SensorType.AGSM_NO2]: "sensorTypes.19",
  [SensorType.AGSM_SO2]: "sensorTypes.20",
  [SensorType.STT_SNAKE]: "sensorTypes.21",
  [SensorType.STT_SNAKE_V2]: "sensorTypes.22",
  [SensorType.JRT_M703A]: "sensorTypes.23",
  [SensorType.JRT_BB2X]: "sensorTypes.24",
  [SensorType.BH1750]: "sensorTypes.25",
  [SensorType.STRAIN_TEMP]: "sensorTypes.26",
  [SensorType.SNAKE_V4]: "sensorTypes.27",
  [SensorType.VCNWM311]: "sensorTypes.28",
  [SensorType.VCNWM511]: "sensorTypes.29",
  [SensorType.GEOSNAKEADXL355]: "sensorTypes.30",
  [SensorType.GEOSNAKEMLX90393]: "sensorTypes.31",
  [SensorType.GEOSNAKEHMC5883L]: "sensorTypes.32",
  [SensorType.GEOSNAKEADC]: "sensorTypes.33",
};

/**
 * Bezpečně vrací lokalizační klíč pro daný typ senzoru.
 * @param type - Hodnota z enumu SensorType
 * @returns String s klíčem pro překlad nebo výchozí hodnotu pro neznámý senzor.
 */
export function getSensorTypeName(type: SensorType): string {
  return sensorTypeNames[type] ?? "sensorTypes.unknown";
}
